import React, {useState, useRef, useMemo, useEffect, useCallback} from "react";
import { useRouter } from "next/router";
import {Box, ResponsiveContext, ThemeContext, Tip, Text} from "grommet";
import {formatCurrency} from "../../../utils/numbers";
import {formatDate} from "../../../utils/dates";
import {getInvoiceStatus} from "../../../utils/business";
import {formatName} from "../../../utils/names";
import DataTableWithBackendSearch from "../../DataTableWithBackendSearch";
import { getQueryString } from "../../../utils/search";
import { DialogContext } from "../../DialogProvider";
import { handleErrors, handleFileDownloadErrors } from "../../../utils/errors";
import { useContext } from "react";
import { NotificationsContext } from "../../NotificationsProvider";
import { DataTablePreferenceContext } from "../../DataTablePreferenceProvider";
import { getInvoiceStatusBadge, getOverdueRange } from "../../../utils/invoice";
import { Dot } from "../../../utils/styled";
import { normalizeColor } from "grommet/utils";
import { setInvoiceNavigationData, setInvoiceBackUrl } from "../../../utils/invoiceNavigation";
import CustomerTag from "../../CustomerTag";
import CustomIcon from "../../CustomIcon";
import Quickbooks from "../../icons/Quickbooks";
import { FIXED_WIDTH_COLUMN_X_SMALL } from "../../../constants/Dimensions";
import InvoiceTag from "../../InvoiceTag";
import InvoiceFilter from "../../filters/InvoiceFilter";
import SearchCustomerFilter from "../../search/filters/SearchCustomerFilter";
import CustomerLabel from "../../CustomerLabel";
import InvoiceLabel from "../../InvoiceLabel";
import {
    faFileInvoiceDollar as IconFileInvoice,
    faSend as IconMailForward,
    faSync as IconSync,
    faBolt as IconQuickPay
} from '@fortawesome/pro-light-svg-icons';
import {
    faBolt as IconQuickPaySolid
} from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from "../../FontAwesomeIcon";
import SendCSVModal from "../../SendCSVModal";
import Button from "../../Button";

const InvoiceList = ({
    title,
    hideCustomer = false,
    dataTableWrapperProps,
    id,
    dataName,
    customerGroupId,
    status,
    ...rest
}) => {
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [showSendCSVModal, setShowSendCSVModal] = useState(false);
    const router = useRouter();

    /*
        Warm the invoice route's chunks up front. Opening a row is a client-side navigation,
        so it does not use the server-rendered HTML at all: Next fetches the route's data and
        renders it here. Cold, that meant ten script chunks downloading on the tap and the
        data request queued behind them, which is most of the wait. One prefetch covers every
        row, since they all resolve to the same route. It is a no-op in development.
    */
    useEffect(() => {
        // Rejects when the chunk has gone, which is normal right after a deploy. Warming the
        // cache is best-effort, so swallow it rather than let it surface as an error report.
        router.prefetch('/invoices/[id]').catch(() => {});
    }, []);
    const notificationCtx = useContext(NotificationsContext);
    const { invoice_state: invoice_state_query, customer_filter: customerFilterQuery } = router.query;
    const dataTablePreferenceCtx = useContext(DataTablePreferenceContext);
    const resetSelectedRowsRef = useRef(null);
    const [isQuickBooksLinked, setIsQuickbooksLinked] = useState(false);
    const theme = useContext(ThemeContext);
    const [csvQueryUrl, setCsvQueryUrl] = useState(); // Used for exporting csv with correct params
    const currentQueryUrl = useRef(null); // Track current query URL for navigation
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    // Embedded in a customer / customer group profile this is still a "tab: all"
    // invoice list, but its remembered sort and page size live in their own bucket so
    // they don't re-sort the top-level invoice list (and vice versa).
    const preferenceTab = hideCustomer ? 'customer_profile' : status;
    const defaultFilter = invoice_state_query ? [invoice_state_query] : dataTablePreferenceCtx.getPreference({table: 'invoices', tab: preferenceTab, key: 'filters'}) ?? [];

    // Parse customer filter from URL on mount
    const parseCustomerFilterFromUrl = useCallback(() => {
        if (!customerFilterQuery) return [];
        return customerFilterQuery.split(',').map(item => {
            const [type, id] = item.split(':');
            return { id: parseInt(id), _type: type === 'group' ? 'group' : 'customer' };
        }).filter(item => item.id);
    }, [customerFilterQuery]);

    const [selectedCustomers, setSelectedCustomers] = useState(() => parseCustomerFilterFromUrl());

    // Sync customer filter to URL
    useEffect(() => {
        if (hideCustomer) return;

        const customerFilter = selectedCustomers.length > 0
            ? selectedCustomers.map(item => {
                const type = item._type === 'group' ? 'group' : 'customer';
                return `${type}:${item.id}`;
            }).join(',')
            : null;

        const currentQuery = { ...router.query };
        if (customerFilter) {
            currentQuery.customer_filter = customerFilter;
        } else {
            delete currentQuery.customer_filter;
        }

        // Build URL without encoding commas
        const queryString = Object.entries(currentQuery)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
        const asPath = queryString ? `${router.pathname}?${queryString}` : router.pathname;

        router.replace(
            { pathname: router.pathname, query: currentQuery },
            asPath,
            { shallow: true }
        );
    }, [selectedCustomers, hideCustomer]);

    // Combined filter component
    const CombinedFilters = ({ filterValues, onApplyFilter, tab }) => (
        <Box direction="row" gap={{ column: 'small', row: 'small' }} wrap>
            <InvoiceFilter filterValues={filterValues} onApplyFilter={onApplyFilter} tab={tab} />
            {!hideCustomer && (
                <SearchCustomerFilter
                    selectedItems={selectedCustomers}
                    onApplyFilter={setSelectedCustomers}
                />
            )}
        </Box>
    );

    const getQuickbooksStatus = (datum) =>{
        if((datum?.quickbooks_error && datum.should_sync)){
            return {
                color: "status-critical",
                status: "Error syncing QuickBooks"
            }
        }

        if(!datum.should_sync && datum.has_exported_to_quickbooks){
            return {
                color: "status-warning",
                status: "Sync disabled"
            }
        }

        if (!datum.has_exported_to_quickbooks){
            return {
                color: "disabled",
                status: "Not exported to QuickBooks"
            }
        }

        return {
            color: "green-strong",
            status: "Exported to QuickBooks"
        }
    }

    const getQuickPayMessage = (datum) => {
        if (datum.billing_status !== 'quickpay') return '';
        if (datum.is_pending_close || datum.invoice_status === "Closed") return "Billed via QuickPay";

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(datum.due_date);
        dueDate.setHours(0, 0, 0, 0);

        if (dueDate.getTime() === today.getTime()) return "QuickPay billing today";
        if (dueDate > today) return `QuickPay scheduled for ${datum.formatted_due_date}`;
        return `Billed via QuickPay on ${datum.formatted_due_date}`;
    }

    const columns = useMemo((showHidden=false) => {
        if (isMobile) return [
            {
                selector: row => row.id,
                cell: (datum) => {
                    const quickpay = datum.billing_status === 'quickpay';
                    return (
                        <Box data-tag="allowRowEvents" fill="horizontal" justify="between" pad={{right: 'xsmall'}}>
                            <Box direction="row" gap="small" data-tag="allowRowEvents">
                                <InvoiceLabel data-tag="allowRowEvents" id={datum?.id} /> • <CustomerLabel customerId={datum?.recipient_id} data-tag="allowRowEvents"/>
                            </Box>
                            <Box data-tag="allowRowEvents">
                                <Text data-tag="allowRowEvents" size="small" color="text-weak">{quickpay ? getQuickPayMessage(datum) : `Due on ${datum?.formatted_due_date}`}</Text>
                                {status !== 'unpaid' ? (
                                    <Text data-tag="allowRowEvents" wordBreak="keep-all"><Text color="text-weak">{formatCurrency(datum?.amount, datum?.currency)}</Text> &middot; {formatCurrency(datum?.open_balance, datum?.currency)} due</Text>
                                ) : (
                                    <Text data-tag="allowRowEvents" wordBreak="keep-all">{formatCurrency(datum?.open_balance ?? datum?.amount, datum?.currency)}</Text>
                                )}
                            </Box>
                        </Box>
                    )
                }
            }
        ];
        return [
            {
                selector: (row, i) => row.billing_status,
                name: (
                    <Box pad={{horizontal: 'xsmall'}}>
                        <FontAwesomeIcon SelectedIcon={IconQuickPay} size="xsmall" />
                    </Box>
                ),
                width: FIXED_WIDTH_COLUMN_X_SMALL,
                cell: datum => {
                    const quickpay = datum.billing_status === 'quickpay';
                    return (
                        <Box>
                            <Tip
                                content={(<Box>{quickpay ? getQuickPayMessage(datum) : ''}</Box>)}>
                                <Box align="center" pad={{horizontal: 'small'}} width={{max: 'xxsmall'}}>
                                    {quickpay && <FontAwesomeIcon SelectedIcon={IconQuickPaySolid} size="xxsmall" />}
                                </Box>
                            </Tip>
                        </Box>
                    );
                }
            },
            isQuickBooksLinked && {
                selector: (row, i) => row.has_exported_to_quickbooks,
                name: (
                    <Box pad={{horizontal: 'xsmall'}}>
                        <CustomIcon SelectedIcon={Quickbooks} color={"green-strong"} />
                    </Box>
                ),
                width: FIXED_WIDTH_COLUMN_X_SMALL,
                cell: datum => {
                    const quickbooksStatus = getQuickbooksStatus(datum);
                    return (
                        <Box>
                            <Tip
                                content={(<Box>{quickbooksStatus.status}</Box>)}>
                                <Box align="center" pad={{horizontal: 'small'}} width={{max: 'xxsmall'}}>
                                    <Dot width="10px" height="10px" color={normalizeColor(quickbooksStatus.color, theme)} />
                                </Box>
                            </Tip>
                        </Box>
                    );
                }
            },
            {
                selector: row => row.issued_at,
                name: 'Invoice Date',
                sortable: true,
                sortField: 'issued_at',
                cell: (datum) => datum.formatted_issued_at,
            },
            {
                selector: row => row.due_date,
                name: 'Due Date',
                sortable: true,
                sortField: 'due_date',
                cell: (datum) => datum?.has_milestones ?
                    (datum?.next_open_milestone ? formatDate(datum.next_open_milestone?.due_date, false, true) : (<Text color="text-weak">No date set</Text>))
                    : datum.formatted_due_date
            },
            {
                selector: row => row.identifier,
                name: 'Invoice #',
                grow: 1.1,
                cell: (datum) => <InvoiceTag id={datum.id} inline />,
                sortField: 'identifier',
                sortable: true,
            },
            !hideCustomer && {
                selector: row => row.company_name,
                name: 'Customer',
                sortable: true,
                grow: 2,
                sortField: 'company_name',
                cell: (datum) => datum?.recipient_id && (<CustomerTag id={datum.recipient_id} inline />)
            },
            showHidden && {
                selector: row => row.subtotal,
                name: 'Subtotal',
                type: "currency",
            },
            showHidden && {
                selector: row => row.discount,
                name: 'Discount',
                type: "currency",
            },
            showHidden && {
                selector: row => row.shipping,
                name: 'Shipping',
                type: "currency",
            },
            showHidden && {
                selector: row => row.tax,
                name: 'Tax',
                type: "currency",
            },
            status !== 'unpaid' && {
                selector: row => row.amount,
                name: 'Amount',
                right: true,
                sortable: true,
                type: "currency",
                sortField: 'amount',
                grow: 1.1,
                cell: (datum) => formatCurrency(datum.amount, datum.currency)
            },
            {
                selector: row => row.open_balance,
                name: 'Balance Due',
                right: true,
                sortable: true,
                type: "currency",
                sortField: 'open_balance',
                wrap: false,
                allowOverflow: false,
                grow: 1.1,
                cell: (datum) => formatCurrency(datum.open_balance, datum.currency)
            },
            {
                selector: row => row.invoice_status,
                name: 'Status',
                cell: datum => getInvoiceStatusBadge({...datum, theme})
            }
        ].filter(Boolean)
    },[isQuickBooksLinked, isMobile, status]);

    const sendInvoiceReminders = () => {
        const values = {
            ids: selectedInvoices.map(invoice => invoice.id)
        };
        fetch(`/api/invoices`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
        }).then(handleErrors)
            .then(() => {
                notificationCtx.showNotification({message: `Reminder${selectedInvoices.length > 1 ? 's' : ''} sent!`});
                resetSelectedRowsRef.current();
            })
            .catch(notificationCtx.showNotification);
    }

    const exportInvoicesToQuickbooks = () => {
        const values = {
            invoice_tokens: selectedInvoices.map(invoice => invoice.invoice_token)
        }
        fetch(`/api/invoices/export-to-quickbooks/bulk-export/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
        }).then(handleErrors)
            .then((data) => {
                notificationCtx.showNotification({message: data.message || `Invoice${selectedInvoices.length > 1 ? 's' : ''} exported!`});
                resetSelectedRowsRef.current(); // Reset Data Table Selected Rows (references to the clearSelectedRows() function in Datatable.js)
            })
            .catch(notificationCtx.showNotification);
    }

    const getInvoicesCSV = ({callback, url, email}) => {
        const exportError = {type: 'error', message: 'Unable to download csv data. Try again later.'};
        const exportSuccessMessage = {type: 'success', message: `Your file will be sent to ${email}`}
        const queryURL = email ? url + `&email=${email}` : url;
        fetch(queryURL, {
            method: 'GET'
        })
            .then(handleFileDownloadErrors)
            .then((data) => {
                data ?
                    notificationCtx.showNotification(exportSuccessMessage) :
                    notificationCtx.showNotification(exportError);
                callback && callback();
            })
            .catch(e => notificationCtx.showNotification(exportError));
    };

    // PDF export functionality for customer/customer group pages
    const canExportPDF = !!(id || customerGroupId);

    const handleExportPDF = async () => {
        // Parse filters from the current CSV query URL
        const queryParams = csvQueryUrl ? csvQueryUrl.split('?')[1] : '';
        const params = new URLSearchParams(queryParams);

        // Build PDF export URL with relevant filters
        const pdfParams = new URLSearchParams();

        // Map filter params to PDF endpoint format
        const dateRange = params.get('date_range');
        const invoiceState = params.get('invoice_state');
        const amountRange = params.get('amount_range');

        if (dateRange) pdfParams.set('date_range', dateRange);
        if (invoiceState) pdfParams.set('invoice_state', invoiceState);
        if (amountRange) pdfParams.set('amount_range', amountRange);

        // If no filters are selected, include all invoices (including paid)
        if (!dateRange && !invoiceState && !amountRange) {
            pdfParams.set('include_paid', 'true');
        }

        // Determine the base URL based on whether it's a customer or customer group
        const baseUrl = customerGroupId
            ? `/api/customers/groups/${customerGroupId}/invoices/pdf`
            : `/api/customers/${id}/invoices/pdf`;

        const pdfUrl = pdfParams.toString()
            ? `${baseUrl}?${pdfParams.toString()}`
            : baseUrl;

        try {
            const response = await fetch(pdfUrl);
            if (!response.ok) {
                throw new Error('Failed to generate PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/);
            link.download = filenameMatch ? filenameMatch[1] : 'invoices.pdf';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notificationCtx.showNotification({
                type: 'error',
                message: 'Unable to download PDF. Try again later.'
            });
        }
    };

    return (
        <Box>
            {showSendCSVModal && (
                <SendCSVModal
                    onClose={()=>{setShowSendCSVModal(false)}}
                    onSubmit={(email) => {
                        getInvoicesCSV({ url: csvQueryUrl, email: email });
                    }}
                />
            )}
            <DataTableWithBackendSearch
                syncFiltersToUrl
                filterParams={{
                    defaultFilter: defaultFilter
                }}
                filterControls={CombinedFilters}
                onRowClicked={row => {
                    setInvoiceBackUrl(router.asPath);
                    router.push(`/invoices/${row.invoice_token}`);
                }}
                columns={columns}
                resetSelectedRowsRef={resetSelectedRowsRef}
                emptyListMessage={`There aren't any${status !== "all" ? ` ${status}` : ""} invoices`}
                title={title}
                dataName={dataName || ''}
                tab={status}
                preferenceTab={preferenceTab}
                // One bucket serves every customer/group profile, so remember the sort
                // and page size but not the search keyword or date/amount range.
                saveSessionState={!hideCustomer}
                key={id}
                noTableHead={isMobile && status !== "unpaid"}
                reshape={(data) => {
                    if (!data) return null;
                    const { has_quickbooks_linked } = data;
                    has_quickbooks_linked && setIsQuickbooksLinked(has_quickbooks_linked);
                    return data.invoices.map((invoice) => {
                        // Safely access window.location only on the client side
                        const protocol = typeof window !== 'undefined' ? window.location.protocol : '';
                        const host = typeof window !== 'undefined' ? window.location.host : '';
                        const {
                            created_at,
                            due_date,
                            amount,
                            open_balance,
                            recipient,
                            order,
                            invoice_token,
                            issued_at,
                            integration
                        } = invoice;
                        const {
                            tax_amount,
                            discount_amount,
                            subtotal_amount,
                            shipping_amount
                        } = order || {};
                        return {
                            ...invoice,
                            formatted_issued_at: formatDate(issued_at || created_at, false, true),
                            created_at: new Date(created_at),
                            formatted_due_date: formatDate(due_date, false, true),
                            due_date: new Date(due_date),
                            amount: parseFloat(amount),
                            open_balance: parseFloat(open_balance),
                            invoice_status: getInvoiceStatus(due_date, open_balance),
                            company_name: recipient?.company_name ? recipient.company_name : formatName(recipient, true, '-'),
                            invoice_link: `${protocol}//${host}/p/${invoice_token}`,
                            overdue_range: getOverdueRange(new Date(due_date)),
                            tax: tax_amount || 0,
                            discount: discount_amount || 0,
                            subtotal: subtotal_amount || 0,
                            shipping: shipping_amount || 0,
                            has_exported_to_quickbooks: integration && integration?.quickbooks_id,
                            quickbooks_error: integration && integration?.quickbooks_error_action,
                            should_sync: integration?.should_sync
                        };
                    });
                }}
                refetchUrlIfTheseParamsChange={{customerGroupId, id, selectedCustomers}}
                getUrl={({filterQuery, ...rest}) => {
                    // Build customer_filter param from selected customers
                    const customerFilter = selectedCustomers.length > 0
                        ? selectedCustomers.map(item => {
                            const type = item._type === 'group' ? 'group' : 'customer';
                            return `${type}:${item.id}`;
                        }).join(',')
                        : null;

                    let params = {
                        invoice_state: (filterQuery || defaultFilter || []).join(','),
                        ...(customerGroupId && { customer_group_id: customerGroupId }),
                        ...(id && !customerGroupId && { customer_id: id }),
                        ...(customerFilter && { customer_filter: customerFilter }),
                        status: status || 'unpaid'
                    }
                    return getQueryString({
                        ...rest,
                        baseUrl: 'api/invoices',
                        optionalParams: params
                    });
                }}
                dataTableWrapperProps={dataTableWrapperProps}
                setQueryUrlCallback={(url) => { // get query url from since data table controls the state of filters and sorting
                    const queryParams = url.split("?")[1]; // get only the url
                    setCsvQueryUrl(`/api/invoices/csv?${queryParams || ''}`);
                    currentQueryUrl.current = url; // Store for navigation
                }}
                onDataChange={(invoices, rawData) => {
                    if (invoices && invoices.length > 0) {
                        const tokens = invoices.map(inv => inv.invoice_token);
                        const total = rawData?.total || invoices.length;
                        const currentOffset = parseInt(new URLSearchParams(currentQueryUrl.current?.split('?')[1] || '').get('offset') || '0');
                        const limit = parseInt(new URLSearchParams(currentQueryUrl.current?.split('?')[1] || '').get('limit') || '20');
                        const hasMore = (currentOffset + invoices.length) < total;
                        setInvoiceNavigationData({
                            tokens,
                            queryUrl: currentQueryUrl.current,
                            hasMore,
                            total
                        });
                    }
                }}
                onExportCSVClicked={()=>{setShowSendCSVModal(true)}}
                showPDFExport={canExportPDF}
                onExportPDFClicked={handleExportPDF}
                icon={IconFileInvoice}
                {...((isQuickBooksLinked || status === "unpaid") ? {
                    selectableRows: true,
                    selectedControls: (
                        <DialogContext.Consumer>
                            {({showDialog}) => (
                                <Box direction="row" gap="xsmall">
                                    {(status === "unpaid") && (
                                        <Button
                                            event={{section: 'invoice_list', element: 'send_reminders'}}
                                            icon={<FontAwesomeIcon SelectedIcon={IconMailForward} size="small" />}
                                            label={"Send reminders"}
                                            gap="xsmall"
                                            onClick={() => {
                                                showDialog({
                                                    title: 'Bulk action: send reminders',
                                                    message: `Are you sure you want to send ${selectedInvoices.length > 1 ? `reminder emails for all ${selectedInvoices.length} invoices` : 'a reminder email for the selected invoice'}?`,
                                                    callback: sendInvoiceReminders,
                                                    width: 'large'
                                                })
                                            }}
                                            hoverIndicator
                                            align="end"
                                            secondary
                                        />
                                    )}
                                    {isQuickBooksLinked && (
                                        <Button
                                            event={{section: 'invoice_list', element: 'sync_to_quickbooks'}}
                                            icon={<FontAwesomeIcon SelectedIcon={IconSync} size="small" />}
                                            label="Sync to QuickBooks"
                                            gap="xsmall"
                                            onClick={() => {
                                                showDialog({
                                                    title: 'Bulk action: Export invoices to QuickBooks',
                                                    message: `Are you sure you want to export ${selectedInvoices.length > 1 ? `all ${selectedInvoices.length} invoices to QuickBooks` : 'the selected invoice to Quickbooks'}?`,
                                                    callback: exportInvoicesToQuickbooks,
                                                    width: 'large'
                                                })
                                            }}
                                            secondary
                                            hoverIndicator
                                            align="end"
                                        />
                                    )}
                                </Box>
                            )}
                        </DialogContext.Consumer>
                    ),
                    onSelectedRowsChange: (data) => data?.selectedRows && setSelectedInvoices(data.selectedRows)
                } : {})}
                {...rest}
            />
        </Box>
    );
};

export default InvoiceList;
